/**
 * Interpolation utility for replacing variables in translation strings
 * Supports {{variable}} syntax
 */

/**
 * Replace variables in a translation string
 * @param {string} template - Translation string with {{variable}} placeholders
 * @param {object} variables - Object containing variable values
 * @returns {string} String with variables replaced
 */
export const interpolate = (template, variables = {}) => {
  if (!template || typeof template !== 'string') {
    return template;
  }

  if (!variables || typeof variables !== 'object') {
    return template;
  }

  // Replace {{variable}} patterns with actual values
  return template.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
    // Check if variable exists in the provided object
    if (variable in variables) {
      const value = variables[variable];
      // Convert to string, handling null/undefined
      return value != null ? String(value) : match;
    }
    
    // If variable not found, return the original placeholder
    return match;
  });
};

/**
 * Extract all variable names from a template string
 * @param {string} template - Translation string with {{variable}} placeholders
 * @returns {string[]} Array of variable names found in template
 */
export const extractVariables = (template) => {
  if (!template || typeof template !== 'string') {
    return [];
  }

  const matches = template.match(/\{\{(\w+)\}\}/g);
  if (!matches) {
    return [];
  }

  // Extract unique variable names
  return [...new Set(matches.map(match => match.replace(/\{\{|\}\}/g, '')))];
};

/**
 * Validate that all required variables are provided
 * @param {string} template - Translation string with {{variable}} placeholders
 * @param {object} variables - Object containing variable values
 * @returns {object} { valid: boolean, missing: string[] }
 */
export const validateInterpolation = (template, variables = {}) => {
  const required = extractVariables(template);
  const provided = Object.keys(variables || {});
  const missing = required.filter(variable => !provided.includes(variable));

  return {
    valid: missing.length === 0,
    missing,
    required,
    provided
  };
};























